import { Component, OnInit } from "@angular/core";
import {
  faFloppyDisk,
  faPrint,
  faEraser,
  faCar,
  faTrash,
  faChain,
} from "@fortawesome/free-solid-svg-icons";
import { NotifierService } from "angular-notifier";
import { FormField } from "src/app/interfaces/form-field.model";
import {
  FinanceInvoiceDeleteDetails,
  FinanceVehicleReceiptService,
} from "src/app/services/autopac/finance-vehicle-receipt.service";

interface FinanceVehicleEntry {
  ContractNo: string;
  InvoiceNo: string;
  RegisterNo: string;
  Name: string;
  InvoiceAmount: number | null;
  LoanAmount: number | null;
  Selected: boolean;
  Install?: number | null;
  NetAtmDisbursed?: number | null;
  checked: boolean;
}

@Component({
  selector: "app-finance-vehicle-receipt",
  templateUrl: "./finance-vehicle-receipt.component.html",
  styleUrls: ["./finance-vehicle-receipt.component.css"],
})
export class FinanceVehicleReceiptComponent implements OnInit {
  faFloppyDisk = faFloppyDisk;
  faPrint = faPrint;
  faEraser = faEraser;
  faCar = faCar;
  faTrash = faTrash;
  faChain = faChain;

  searchBooking: FormField[] = [];
  buyersDetails: FormField[] = [];
  selectedFinanceCompany = "";
  showCheckboxTable = true;
  showTrashTable = false;
  loadingVehicles = false;
  loadingReceipt = false;
  saving = false;
  deleting = false;
  printReady = false;
  receiptNoToPrint = "";
  balanceLabel = "Balance";

  tableHeadersWithCheckbox = [
    { title: "CONTRACT NO", type: "data", id: "ContractNo" },
    { title: "INVOICE NO", type: "data", id: "InvoiceNo" },
    { title: "REGISTER NO", type: "data", id: "RegisterNo" },
    { title: "CUSTOMER NAME", type: "data", id: "Name" },
    { title: "INVOICE AMT", type: "data", id: "InvoiceAmount" },
    { title: "LOAN AMT", type: "data", id: "LoanAmount" },
    { title: "ACTION", type: "checkbox", id: "checked" },
  ];

  tableHeadersWithTrash = [
    { title: "CONTRACT NO", type: "data", id: "ContractNo" },
    { title: "INVOICE NO", type: "data", id: "InvoiceNo" },
    { title: "REGISTER NO", type: "data", id: "RegisterNo" },
    { title: "CUSTOMER NAME", type: "data", id: "Name" },
    { title: "INVOICE AMT", type: "data", id: "InvoiceAmount" },
    { title: "LOAN AMT", type: "data", id: "LoanAmount" },
    { title: "ACTION", type: "trash", id: "delete" },
  ];

  tableInvoiceSelectionEntries: FinanceVehicleEntry[] = [];
  tableWithTrashEntries: FinanceVehicleEntry[] = [];

  constructor(
    private financeReceiptService: FinanceVehicleReceiptService,
    private notifier: NotifierService,
  ) {}

  ngOnInit(): void {
    this.searchBooking = [
      {
        type: "select",
        id: "FinanceCompany",
        label: "Finance Company",
        required: true,
        options: [],
      },
      {
        type: "search",
        id: "searchInvoiceNumber",
        label: "Invoice Number",
        searchEvent: true,
        searchType: "InvoiceNo",
      },
      {
        type: "search",
        id: "searchContractNumber",
        label: "Contract Number",
        searchEvent: true,
        searchType: "ContractNo",
      },
      {
        type: "search",
        id: "searchRegisterNumber",
        label: "Registration Number",
        searchEvent: true,
        searchType: "RegisterNo",
      },
      {
        type: "search",
        id: "searchBuyerName",
        label: "Buyer Name",
        searchEvent: true,
        searchType: "BuyerName",
      },
      { type: "text", id: "searchReceiptNumber", label: "Receipt Number" },
    ];

    this.resetBuyerDetails();
    this.financeReceiptService.getBankList().subscribe({
      next: (response) => {
        if (response.statusCode !== 200 || !Array.isArray(response.body)) {
          this.notifier.notify(
            "error",
            response.message || "Unable to retrieve finance companies.",
          );
          return;
        }
        const financeCompanyField = this.searchBooking.find(
          (field) => field.id === "FinanceCompany",
        );
        if (financeCompanyField) {
          financeCompanyField.options = response.body.map((bank: string) => ({
            display: bank,
            value: bank,
          }));
        }
      },
      error: () =>
        this.notifier.notify("error", "Unable to retrieve finance companies."),
    });
  }

  updateFields(event: { fields: FormField[] }): void {
    this.buyersDetails = event.fields;
    const amountField = this.buyersDetails.find(
      (field) => field.id === "amountReceivedfromFinance",
    );
    const hasAmount =
      amountField?.value !== undefined && amountField.value !== null && amountField.value !== "";
    const amount = hasAmount ? Number(amountField?.value) : 0;
    if (hasAmount && amountField && Number.isFinite(amount)) {
      const roundedAmount = Math.round(amount * 100) / 100;
      amountField.value = roundedAmount;
    }

    const loan = this.numberFieldValue("totalLoanAmountPayable");
    const amountReceived = hasAmount ? this.numberFieldValue("amountReceivedfromFinance") : 0;
    const difference = amountReceived - loan;
    const shortField = this.buyersDetails.find(
      (field) => field.id === "shortPayment",
    );
    this.balanceLabel = hasAmount
      ? difference < 0
        ? "Short Payment"
        : difference > 0
          ? "Excess Payment"
          : "Balance"
      : "Balance";
    if (shortField) {
      shortField.value = hasAmount ? Math.abs(difference) : "";
      shortField.label = this.balanceLabel;
    }

    const wordsField = this.buyersDetails.find(
      (field) => field.id === "words",
    );
    if (wordsField) {
      const words = hasAmount ? this.convertAmountToWords(amountReceived) : "";
      wordsField.value = words ? `${words} ONLY.` : "";
    }
  }

  private convertAmountToWords(amount: number): string {
    if (!Number.isFinite(amount) || amount < 0) return "";
    const rounded = Math.round(amount * 100) / 100;
    const whole = Math.floor(rounded);
    const cents = Math.round((rounded - whole) * 100);
    const wholeWords = this.integerToWords(whole);
    if (!wholeWords) return "";
    if (!cents) return wholeWords;
    const centsWords = this.integerToWords(cents);
    return `${wholeWords} AND CENT${cents > 9 ? "S" : ""} ${centsWords}`;
  }

  private integerToWords(value: number): string {
    const ones = [
      "ZERO", "ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX", "SEVEN",
      "EIGHT", "NINE", "TEN", "ELEVEN", "TWELVE", "THIRTEEN", "FOURTEEN",
      "FIFTEEN", "SIXTEEN", "SEVENTEEN", "EIGHTEEN", "NINETEEN",
    ];
    const tens = [
      "", "", "TWENTY", "THIRTY", "FORTY", "FIFTY", "SIXTY", "SEVENTY",
      "EIGHTY", "NINETY",
    ];
    if (!Number.isInteger(value) || value < 0 || value >= 1_000_000_000)
      return "";
    if (value < 20) return ones[value];
    if (value < 100)
      return tens[Math.floor(value / 10)] + (value % 10 ? ` ${ones[value % 10]}` : "");
    if (value < 1000)
      return (
        `${ones[Math.floor(value / 100)]} HUNDRED` +
        (value % 100 ? ` ${this.integerToWords(value % 100)}` : "")
      );
    if (value < 1_000_000)
      return (
        `${this.integerToWords(Math.floor(value / 1000))} THOUSAND` +
        (value % 1000 ? ` ${this.integerToWords(value % 1000)}` : "")
      );
    return (
      `${this.integerToWords(Math.floor(value / 1_000_000))} MILLION` +
      (value % 1_000_000
        ? ` ${this.integerToWords(value % 1_000_000)}`
        : "")
    );
  }

  private numberFieldValue(id: string): number {
    const value = this.buyersDetails.find((field) => field.id === id)?.value;
    const number = Number(value ?? 0);
    return Number.isFinite(number) ? number : 0;
  }

  hasSelectedEntries(): boolean {
    return this.tableInvoiceSelectionEntries.some((item) => item.checked);
  }

  save(): void {
    if (this.saving || this.loadingReceipt || this.printReady) return;
    const finAmountValue = this.buyersDetails.find(
      (field) => field.id === "amountReceivedfromFinance",
    )?.value;
    const finAmount = Number(finAmountValue);
    const finDate = this.formatDateToDDMMYYYY(
      String(
        this.buyersDetails.find((field) => field.id === "receiptDate")?.value ??
          "",
      ),
    );
    const prevReceiptNo = Number(
      this.buyersDetails.find(
        (field) => field.id === "previousFinanceReceiptNumber",
      )?.value,
    );
    const paymentMode = String(
      this.buyersDetails.find((field) => field.id === "paymentMode")?.value ?? "",
    ).trim();

    if (
      !this.showTrashTable ||
      !this.tableWithTrashEntries.length ||
      !Number.isFinite(prevReceiptNo) ||
      !Number.isFinite(finAmount) ||
      finAmount <= 0 ||
      finAmount > 99_999_999 ||
      !paymentMode ||
      !/^\d{2}\/\d{2}\/\d{4}$/.test(finDate)
    ) {
      this.notifier.notify(
        "error",
        "Select invoices and enter the receipt date, amount and payment mode before saving.",
      );
      return;
    }

    const items = this.tableWithTrashEntries.map((item) => ({
      __type: "KAPS.Model.Autopac.CrmFinanceVehicle",
      ContractNo: item.ContractNo,
      InvoiceNo: item.InvoiceNo,
      RegisterNo: item.RegisterNo,
      Name: item.Name,
      InvoiceAmount: item.InvoiceAmount,
      LoanAmount: item.LoanAmount,
      Selected: true,
      Install: item.Install ?? null,
      NetAtmDisbursed: item.NetAtmDisbursed ?? null,
    }));
    const payload = {
      model: {
        FININVOICENO: prevReceiptNo + 1,
        FINAMOUNT: finAmount.toFixed(2),
        FINDATE: finDate,
        FINWRORD: this.buyersDetails.find((field) => field.id === "words")?.value ?? "",
        FINBANK:
          this.searchBooking.find((field) => field.id === "FinanceCompany")?.value ??
          "",
        MODETYPE: paymentMode,
        CHEQUENO:
          this.buyersDetails.find((field) => field.id === "chequeNumber")?.value ??
          "",
        ITEMS: items,
      },
    };

    this.saving = true;
    this.financeReceiptService.saveFinanceInvoice(payload).subscribe({
      next: (response) => {
        this.saving = false;
        if (this.responseCode(response) !== 0) {
          this.notifier.notify(
            "error",
            this.responseMessage(response) || "Unable to save the finance receipt.",
          );
          return;
        }

        this.receiptNoToPrint =
          String(response.body?.Message ?? payload.model.FININVOICENO);
        this.printReady = true;
        this.tableWithTrashEntries = [];
        this.tableInvoiceSelectionEntries = [];
        this.showTrashTable = false;
        this.showCheckboxTable = true;
        this.setDetailsEditable(false);
        this.notifier.notify(
          "success",
          `Finance receipt no. ${this.receiptNoToPrint} is generated.`,
        );
      },
      error: () => {
        this.saving = false;
        this.notifier.notify("error", "Unable to save the finance receipt.");
      },
    });
  }

  private responseCode(response: any): number | undefined {
    return response?.code ?? response?.body?.Code ?? response?.body?.code;
  }

  private responseMessage(response: any): string {
    return response?.message ?? response?.body?.Message ?? "";
  }

  private formatDateToDDMMYYYY(dateValue: string): string {
    if (!dateValue) return "";
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) return dateValue;
    const [year, month, day] = dateValue.split("-");
    return year && month && day ? `${day}/${month}/${year}` : "";
  }

  print(): void {
    if (!this.printReady || !this.receiptNoToPrint) return;
    this.printReceiptNumber(this.receiptNoToPrint, true);
  }

  printExistingReceipt(): void {
    const receiptNo = String(
      this.searchBooking.find((field) => field.id === "searchReceiptNumber")
        ?.value ?? "",
    ).trim();
    if (!receiptNo) {
      this.notifier.notify("error", "Enter a receipt number to print.");
      return;
    }
    this.printReceiptNumber(receiptNo, false);
  }

  private printReceiptNumber(receiptNo: string, clearAfterPrint: boolean): void {
    this.financeReceiptService
      .printByReceiptNo(receiptNo)
      .subscribe({
        next: (response) => {
          const url =
            response?.body?.Data ??
            response?.body?.data ??
            response?.body?.Value ??
            response?.body?.d?.Value;
          if (this.responseCode(response) !== 0 || !url) {
            this.notifier.notify(
              "error",
              this.responseMessage(response) || "Unable to print the receipt.",
            );
            return;
          }
          window.open(url, "_blank", "noopener");
          if (clearAfterPrint) this.clear();
        },
        error: () =>
          this.notifier.notify("error", "Unable to print the receipt."),
      });
  }

  next(): void {
    if (this.loadingReceipt || this.saving || this.printReady) return;
    const selectedItems = this.tableInvoiceSelectionEntries.filter(
      (entry) => entry.checked,
    );
    if (!selectedItems.length) {
      this.notifier.notify("error", "Select at least one invoice to continue.");
      return;
    }

    this.loadingReceipt = true;
    this.financeReceiptService.getLastReceipt().subscribe({
      next: (response) => {
        this.loadingReceipt = false;
        const lastReceipt = response?.body;
        if (
          response.statusCode !== 200 ||
          !lastReceipt?.Name ||
          !lastReceipt?.Value
        ) {
          this.notifier.notify("error", "Unable to retrieve the last receipt.");
          return;
        }

        this.tableWithTrashEntries = selectedItems;
        this.showCheckboxTable = false;
        this.showTrashTable = true;
        this.setDetailsEditable(true);
        const previousReceipt = this.buyersDetails.find(
          (field) => field.id === "previousFinanceReceiptNumber",
        );
        if (previousReceipt) previousReceipt.value = lastReceipt.Name;
        const receiptDate = this.buyersDetails.find(
          (field) => field.id === "receiptDate",
        );
        if (receiptDate) receiptDate.value = this.convertDate(lastReceipt.Value);

        const totalLoanAmount = selectedItems.reduce(
          (sum, item) => sum + Number(item.LoanAmount ?? 0),
          0,
        );
        const loanField = this.buyersDetails.find(
          (field) => field.id === "totalLoanAmountPayable",
        );
        if (loanField) loanField.value = totalLoanAmount;
        this.updateFields({ fields: this.buyersDetails });
      },
      error: () => {
        this.loadingReceipt = false;
        this.notifier.notify("error", "Unable to retrieve the last receipt.");
      },
    });
  }

  removeSelectedEntry(entry: FinanceVehicleEntry): void {
    entry.checked = false;
    entry.Selected = false;
    this.tableWithTrashEntries = this.tableWithTrashEntries.filter(
      (item) => item !== entry,
    );
    if (!this.tableWithTrashEntries.length) {
      this.showTrashTable = false;
      this.showCheckboxTable = true;
      this.setDetailsEditable(false);
    }
    const total = this.tableWithTrashEntries.reduce(
      (sum, item) => sum + Number(item.LoanAmount ?? 0),
      0,
    );
    const loanField = this.buyersDetails.find(
      (field) => field.id === "totalLoanAmountPayable",
    );
    if (loanField) loanField.value = total;
    this.updateFields({ fields: this.buyersDetails });
  }

  clear(): void {
    this.searchBooking = this.searchBooking.map((field) => ({
      ...field,
      value: "",
    }));
    this.selectedFinanceCompany = "";
    this.tableInvoiceSelectionEntries = [];
    this.tableWithTrashEntries = [];
    this.showCheckboxTable = true;
    this.showTrashTable = false;
    this.loadingVehicles = false;
    this.loadingReceipt = false;
    this.saving = false;
    this.printReady = false;
    this.receiptNoToPrint = "";
    this.balanceLabel = "Balance";
    this.resetBuyerDetails();
  }

  private resetBuyerDetails(): void {
    this.buyersDetails = [
      {
        type: "text",
        id: "previousFinanceReceiptNumber",
        label: "Previous Finance Receipt Number",
        lock: true,
      },
      {
        type: "date",
        id: "receiptDate",
        label: "Receipt Date (dd/mm/yyyy)",
        lock: true,
      },
      {
        type: "number",
        id: "amountReceivedfromFinance",
        label: "Amount received from finance",
        lock: true,
        step: 0.01,
      },
      { type: "text", id: "words", label: "Words", lock: true },
      { type: "text", id: "paymentMode", label: "Payment Mode", lock: true },
      { type: "text", id: "chequeNumber", label: "Cheque Number", lock: true },
      {
        type: "text",
        id: "totalLoanAmountPayable",
        label: "Total Loan Amount Payable",
        lock: true,
      },
      {
        type: "number",
        id: "shortPayment",
        label: this.balanceLabel,
        lock: true,
      },
    ];
  }

  private setDetailsEditable(editable: boolean): void {
    this.buyersDetails = this.buyersDetails.map((field) => ({
      ...field,
      lock:
        field.id === "previousFinanceReceiptNumber" ||
        field.id === "totalLoanAmountPayable" ||
        field.id === "shortPayment"
          ? true
          : !editable,
    }));
  }

  updateFieldsMainForm(event: { name: string; fields: FormField[] }): void {
    if (event.name !== "searchBooking") return;
    this.searchBooking = event.fields;
    const selectedValue = String(
      this.searchBooking.find((field) => field.id === "FinanceCompany")?.value ??
        "",
    );
    if (selectedValue === this.selectedFinanceCompany) return;

    this.selectedFinanceCompany = selectedValue;
    this.resetReceiptWorkflow();
    if (!selectedValue) return;

    this.loadingVehicles = true;
    this.financeReceiptService
      .getFinanceVehicle(selectedValue, "FinCode", "")
      .subscribe({
        next: (response) => {
          this.loadingVehicles = false;
          this.setVehicleResults(response);
        },
        error: () => {
          this.loadingVehicles = false;
          this.notifier.notify("error", "Unable to retrieve finance vehicles.");
        },
      });
  }

  setSearchValue(fieldId: string, value: string): void {
    const field = this.searchBooking.find((item) => item.id === fieldId);
    if (field) field.value = value;
  }

  handleSearch(event: { fieldId: string }): void {
    const field = this.searchBooking.find(
      (item) => item.id === event.fieldId,
    );
    if (!field?.searchType) return;

    const financeCompany = String(
      this.searchBooking.find((item) => item.id === "FinanceCompany")?.value ??
        "",
    );
    const searchText = String(field.value ?? "").trim();
    if (!financeCompany) {
      this.notifier.notify("error", "Select a finance company before searching.");
      return;
    }
    if (!searchText) {
      this.notifier.notify("error", "Enter a value to search.");
      return;
    }

    this.resetReceiptWorkflow();
    this.loadingVehicles = true;
    this.financeReceiptService
      .getFinanceVehicle(financeCompany, field.searchType, searchText)
      .subscribe({
        next: (response) => {
          this.loadingVehicles = false;
          this.setVehicleResults(response);
        },
        error: () => {
          this.loadingVehicles = false;
          this.notifier.notify("error", "Unable to search finance vehicles.");
        },
      });
  }

  private setVehicleResults(response: any): void {
    if (response.statusCode !== 200 || !Array.isArray(response.body)) {
      this.tableInvoiceSelectionEntries = [];
      this.notifier.notify(
        "error",
        response.message || "Unable to retrieve finance vehicles.",
      );
      return;
    }
    this.tableInvoiceSelectionEntries = response.body.map(
      (item: Omit<FinanceVehicleEntry, "checked">) => ({
        ...item,
        checked: false,
        Selected: false,
      }),
    );
    if (!this.tableInvoiceSelectionEntries.length) {
      this.notifier.notify("info", "No unpaid invoices found.");
    }
  }

  private resetReceiptWorkflow(): void {
    this.tableInvoiceSelectionEntries = [];
    this.tableWithTrashEntries = [];
    this.showCheckboxTable = true;
    this.showTrashTable = false;
    this.printReady = false;
    this.receiptNoToPrint = "";
    this.balanceLabel = "Balance";
    this.resetBuyerDetails();
  }

  private convertDate(dateString: string): string {
    const [day, month, year] = dateString.split("/");
    return year && month && day ? `${year}-${month}-${day}` : "";
  }

  deleteInvoice(): void {
    if (this.deleting) return;
    const invoiceNo = String(
      this.searchBooking.find((field) => field.id === "searchInvoiceNumber")
        ?.value ?? "",
    ).trim();
    if (!invoiceNo) {
      this.notifier.notify("error", "Enter an invoice number to delete.");
      return;
    }
    if (!window.confirm(`Delete invoice ${invoiceNo}?`)) return;

    const previousReceiptNo = this.numberFieldValue(
      "previousFinanceReceiptNumber",
    );
    const receiptDate = String(
      this.buyersDetails.find((field) => field.id === "receiptDate")?.value ??
        "",
    );
    const deleteDetails: FinanceInvoiceDeleteDetails = {
      TotalLoanAmount: this.numberFieldValue("totalLoanAmountPayable"),
      PrevReceiptNo: previousReceiptNo,
      ReceiptDate: this.formatDateToDDMMYYYY(receiptDate),
    };

    this.deleting = true;
    this.financeReceiptService.deleteInvoice(deleteDetails).subscribe({
      next: (response) => {
        this.deleting = false;
        if (this.responseCode(response) !== 0) {
          this.notifier.notify(
            "error",
            this.responseMessage(response) || "No invoice was deleted.",
          );
          return;
        }
        const field = this.searchBooking.find(
          (item) => item.id === "searchInvoiceNumber",
        );
        if (field) field.value = "";
        this.notifier.notify("success", "Invoice deleted successfully.");
        if (this.selectedFinanceCompany) {
          this.loadingVehicles = true;
          this.financeReceiptService
            .getFinanceVehicle(this.selectedFinanceCompany, "FinCode", "")
            .subscribe({
              next: (result) => {
                this.loadingVehicles = false;
                this.setVehicleResults(result);
              },
              error: () => {
                this.loadingVehicles = false;
                this.notifier.notify(
                  "error",
                  "Invoice was deleted, but the unpaid invoice list could not be refreshed.",
                );
              },
            });
        }
      },
      error: () => {
        this.deleting = false;
        this.notifier.notify("error", "Unable to delete the finance invoice.");
      },
    });
  }
}
